import { useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { abgleichFaellig, de, type List, type PartnerConnection, type WeekPlanResult , type Store} from "@ainkauf/core";
import { BottomNav } from "@/components/BottomNav";
import { PantryList } from "@/components/plan/PantryList";
import { PlanPreview } from "@/components/plan/PlanPreview";
import { RecipeList } from "@/components/plan/RecipeList";
import { WeekBoard } from "@/components/plan/WeekBoard";
import { allLists, allStores, ensureDefaultList, lowStockToList, partnerConnection, planToListSortiert, previewWeek } from "@/db/repository";
import { gleicheAb } from "@/lib/partner";

type Reiter = "woche" | "rezepte" | "vorrat";

/**
 * Planen: Woche, Rezepte, Vorrat.
 *
 * Drei Ansichten hinter einem Reiter statt drei Einträgen in der unteren
 * Leiste. Vier Felder unten sind noch bedienbar, sieben wären es nicht, und
 * die drei gehören ohnehin zusammen: Der Wochenplan braucht Rezepte, und die
 * Rezepte fragen den Vorrat.
 */
export function PlanScreen() {
  const [reiter, setReiter] = useState<Reiter>("woche");
  const [vorschau, setVorschau] = useState<WeekPlanResult | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const listen = useLiveQuery(() => allLists(), [], [] as List[]);
  // Für die Vorschau: Eine Liste kann über ihren Laden zu einem Ladentyp gehören.
  const laeden = useLiveQuery(() => allStores(), [], [] as Store[]);
  const verbindung = useLiveQuery(() => partnerConnection(), [], undefined as PartnerConnection | null | undefined);

  useEffect(() => {
    void ensureDefaultList();
  }, []);

  /*
    Stiller Abgleich beim Öffnen.

    Ohne Ladeanzeige und ohne Fehlermeldung: Die Rezepte stehen längst in
    Dexie, der Abgleich holt nur, was drüben seither passiert ist, und die
    neuen Zeilen kommen über Realtime von selbst ins Bild. Wer nichts geändert
    hat, merkt nichts, und wer ohne Netz plant, soll keine rote Zeile sehen.

    Einmal je Öffnen, nicht bei jedem Neuzeichnen: Das Fällig-Sein hängt an
    last_sync_at, und das ändert sich durch genau diesen Aufruf.
  */
  // Eine Referenz, kein Zustand: Ob schon abgeglichen wurde, ändert am Bild
  // nichts und soll kein Neuzeichnen auslösen.
  const abgeglichen = useRef(false);
  useEffect(() => {
    if (abgeglichen.current || verbindung === undefined) return;
    const faellig = abgleichFaellig({
      verbunden: verbindung !== null && verbindung.status === "verbunden",
      online: navigator.onLine,
      lastSyncAt: verbindung?.last_sync_at ?? null,
    });
    if (!faellig) return;
    abgeglichen.current = true;
    void gleicheAb();
  }, [verbindung]);

  function melde(text: string) {
    setMeldung(text);
    window.setTimeout(() => {
      setMeldung(null);
    }, 4000);
  }

  const reiterListe = [
    { key: "woche" as const, label: de.plan.tabWeek },
    { key: "rezepte" as const, label: de.plan.tabRecipes },
    { key: "vorrat" as const, label: de.plan.tabPantry },
  ];

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-4 px-4 mit-leiste">
      <header className="pt-5">
        <h1 className="text-[2rem]">{de.plan.title}</h1>
      </header>

      {/* Segmente statt Reiter mit Unterstrich: eine Fläche, drei Felder,
          jedes über 44 Pixel hoch und mit dem Daumen zu treffen. */}
      <div className="flex gap-1 rounded-full bg-paper-2 p-1" role="tablist" aria-label={de.plan.title}>
        {reiterListe.map((eintrag) => {
          const aktiv = eintrag.key === reiter;
          return (
            <button
              key={eintrag.key}
              type="button"
              role="tab"
              aria-selected={aktiv}
              onClick={() => {
                setReiter(eintrag.key);
              }}
              data-testid={`plan-tab-${eintrag.key}`}
              className={`min-h-[44px] flex-1 rounded-full text-sm font-bold transition-colors ${
                aktiv ? "bg-card text-ink shadow-[var(--shadow-card)]" : "text-ink-2"
              }`}
            >
              {eintrag.label}
            </button>
          );
        })}
      </div>

      {meldung !== null && (
        <p className="rounded-[var(--radius-lg)] bg-herb/12 px-4 py-3 text-sm font-semibold text-herb" data-testid="plan-toast">
          {meldung}
        </p>
      )}

      {reiter === "woche" && (
        <WeekBoard
          onPlanToList={(tage) => {
            void previewWeek(tage).then(setVorschau);
          }}
        />
      )}
      {reiter === "rezepte" && <RecipeList />}
      {reiter === "vorrat" && (
        <PantryList
          onRestock={() => {
            void nachkaufen();
          }}
        />
      )}

      {vorschau !== null && (
        <PlanPreview
          plan={vorschau}
          lists={listen}
          stores={laeden}
          onConfirm={async (listId, zeilen) => {
            const { angelegt, verteilt } = await planToListSortiert({ ...vorschau, missing: zeilen }, listId);
            setVorschau(null);
            // Was in eine andere Liste wanderte, wird gesagt: Sonst sucht
            // jemand die Sojasauce im Supermarkt.
            melde(verteilt.length > 0 ? `${de.planning.added(angelegt)}, ${de.items.movedSeveral(verteilt.length)}` : de.planning.added(angelegt));
          }}
          onCancel={() => {
            setVorschau(null);
          }}
        />
      )}

      <BottomNav active="planen" />
    </div>
  );

  async function nachkaufen() {
    const liste = listen[0] ?? (await ensureDefaultList(true));
    if (!liste) return;
    const anzahl = await lowStockToList(liste.id);
    melde(de.planning.added(anzahl));
  }
}
